import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-all duration-200 overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground shadow-sm [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground shadow-sm [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white shadow-sm [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground border-border/80 [a&]:hover:bg-accent [a&]:hover:text-accent-foreground [a&]:hover:border-accent',
        success:
          'border-transparent bg-success/90 text-white dark:bg-success dark:text-white shadow-sm [a&]:hover:bg-success [a&]:dark:hover:bg-success/90 focus-visible:ring-success/20 dark:focus-visible:ring-success/40',
        warning:
          'border-transparent bg-warning/90 text-amber-950 dark:bg-warning dark:text-white shadow-sm [a&]:hover:bg-warning [a&]:dark:hover:bg-warning/90 focus-visible:ring-warning/20 dark:focus-visible:ring-warning/40',
        info: 'border-transparent bg-info/90 text-white dark:bg-info dark:text-white shadow-sm [a&]:hover:bg-info [a&]:dark:hover:bg-info/90 focus-visible:ring-info/20 dark:focus-visible:ring-info/40',
        technical:
          'border-border/60 bg-muted/50 text-muted-foreground font-mono text-[10px] [a&]:hover:bg-muted [a&]:hover:text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
